import { getUID } from '@redviking/argonaut-util/src/getUID';
import { UiFlowNodeControl } from '../../controls/UiFlowNodeControl';
import { createNodeSocket } from '../../sync-utils/create-node-socket';
import { removeNodeSocket } from '../../sync-utils/remove-node-socket';
import type { UiNodeSpec } from './UiNodeSpec';
import type { RequiredSockets } from './UiNodeSpec';
import { type NodeSocketKindMap, nodeCfgSchemaMap } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';

export const jsonAggUiNodeSpec: UiNodeSpec<'jsonAgg'> = {
    requiredSockets: (node, _, availableOutputSockets) => {
        const ret: ReturnType<RequiredSockets<'jsonAgg', NodeSocketKindMap['jsonAgg']>> = {
            required: [ 'trigger', 'input', 'next' ],
            conditionallyRequired: [
                {
                    kind: 'or',
                    availableOptions: availableOutputSockets.filter(s => s !== 'next'),
                },
            ],
        };
        return ret;
    },
    cfg: () => ({ controlValues: { outputExpressions: [] }, id: getUID(), kind: 'jsonAgg', disabled: false }),
    controls: getNodeCfg => ({
        outputExpressions: {
            cfg: {
                kind: 'button',
                label: 'Add Output',
                onClick: () => {
                    const nodeCfg = getNodeCfg();
                    createNodeSocket({
                        newNodeCfg: {
                            ...nodeCfg,
                            controlValues: {
                                ...nodeCfg.controlValues,
                                outputExpressions: [
                                    ...nodeCfg.controlValues.outputExpressions,
                                    '',
                                ],
                            },
                        },
                    });
                },
            },
            nodeCfg: getNodeCfg(),
            value: getNodeCfg().controlValues.outputExpressions,
        },
    }),
    inputs: () => ({
        trigger: { socket: 'event', label: 'Trigger' },
        reset: { socket: 'event', label: 'Reset' },
        input: { socket: 'string', label: 'JSON Patch' },
    }),
    kindLabel: 'JSON Object Aggregation',
    nodeCategory: 'variable',
    outputs: nodeCfg => {
        const outputs: ReturnType<UiNodeSpec<'jsonAgg'>['outputs']> = {
            merged: { socket: 'string', label: 'Full Object (stringified)' },
            next: { socket: 'event', label: 'Next' },
        };
        for (let i = 0; i < nodeCfg.controlValues.outputExpressions.length; i++) {
            const control: UiFlowNodeControl.Opts<string> = {
                nodeCfg,
                socketDefault: false,
                value: nodeCfg.controlValues.outputExpressions[i],
                cfg: {
                    placeholder: 'Object key',
                    getValue: nodeCfg => {
                        if (nodeCfg.kind !== 'jsonAgg') {
                            return null;
                        }
                        return nodeCfg.controlValues.outputExpressions[i];
                    },
                    onChange: newValue => {
                        const newOutputExpressions = [ ...nodeCfg.controlValues.outputExpressions ];
                        newOutputExpressions[i] = newValue;

                        createNodeSocket({
                            newNodeCfg: {
                                ...nodeCfg,
                                controlValues: {
                                    ...nodeCfg.controlValues,
                                    outputExpressions: newOutputExpressions,
                                },
                            },
                        });
                    },
                    kind: 'deletableText',
                    onDelete () {
                        removeNodeSocket({
                            newNodeCfg: {
                                ...nodeCfg,
                                controlValues: {
                                    ...nodeCfg.controlValues,
                                    outputExpressions: [
                                        ...nodeCfg.controlValues.outputExpressions.slice(0, i),
                                        ...nodeCfg.controlValues.outputExpressions.slice(i + 1),
                                    ],
                                },
                            },
                            direction: 'output',
                            removalMethod: {
                                index: i,
                                prefix: 'output',
                                kind: 'indexedIdentifier',
                            },
                        });
                    },
                },
            };
            outputs[`output${i}`] = { control, socket: 'varValue' };
        }
        return outputs;
    },
    validator: node => {
        const errors: Record<string, string> = {};
        if (!node.controlValues.outputExpressions.length) {
            errors.outputExpressions = 'At least one output expression is required';
        }
        for (let i = 0; i < node.controlValues.outputExpressions.length; i++) {
            const jsonExpression = node.controlValues.outputExpressions[i];
            if (!jsonExpression) {
                errors[`output${i}`] = 'Output expression is required';
            } else {
                // I think there is a call to safe parse on the entire config that is occuring elsewhere essentially duplicating this check
                const parseResult = nodeCfgSchemaMap.jsonParse.shape.controlValues.shape.outputExpressions.safeParse([ jsonExpression ]);
                if (parseResult.success === false) {
                    errors[`output${i}`] = 'This is not a valid expression';
                }
            }
        }
        return Object.keys(errors).length ? errors : true;
    },
};
